import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Wallet, Lock, Unlock, TrendingUp, ArrowRight, History } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";

export const WalletWidget = ({ className = "" }) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/wallet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWallet(response.data);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={`${className} animate-pulse`}>
        <CardContent className="p-6">
          <div className="h-32 bg-slate-100 rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!wallet) return null;

  const availablePercent = wallet.total_balance > 0 
    ? (wallet.available_balance / wallet.total_balance) * 100 
    : 0;

  const nextUnlock = wallet.locked_items?.[0];

  return (
    <Card className={`${className} overflow-hidden`} data-testid="wallet-widget">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-sidebar-bg to-sidebar-bg-end p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/80 font-medium">Your Wallet</span>
          </div>
          <Link to="/wallet">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">
              <History className="w-4 h-4 mr-1" /> History
            </Button>
          </Link>
        </div>
        <div>
          <p className="text-white/60 text-sm mb-1">Total Balance</p>
          <p className="text-3xl font-bold text-white" data-testid="wallet-total-balance">
            ₹{wallet.total_balance?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <CardContent className="p-6 space-y-6">
        {/* Balance Breakdown */}
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="h-3 rounded-full bg-wallet-locked/20 overflow-hidden">
              <div 
                className="h-full bg-wallet-available rounded-full transition-all duration-500"
                style={{ width: `${availablePercent}%` }}
              />
            </div>
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                <Unlock className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Available</p>
                <p className="font-semibold text-slate-900" data-testid="wallet-available-balance">
                  ₹{wallet.available_balance?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Locked</p>
                <p className="font-semibold text-slate-900" data-testid="wallet-locked-balance">
                  ₹{wallet.locked_balance?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Unlock Info */}
        {nextUnlock && (
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">
                  Next Unlock: <span className="font-medium">{nextUnlock.type?.replace('_', ' ')}</span>
                </p>
                <p className="text-lg font-bold text-primary">
                  +₹{nextUnlock.monthly_unlock?.toLocaleString() || 0}/month
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Unlocking Progress</span>
                <span>
                  {Math.round((nextUnlock.unlocked_amount || 0) / nextUnlock.amount * 100)}%
                </span>
              </div>
              <Progress 
                value={(nextUnlock.unlocked_amount || 0) / nextUnlock.amount * 100} 
                className="h-2"
              />
            </div>
          </div>
        )}

        {/* View Details Link */}
        <Link to="/wallet" className="block">
          <Button variant="outline" className="w-full gap-2" data-testid="wallet-view-details-btn">
            View Full Details
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};
